/**
 * WHAT THIS BROWSER CAN ACTUALLY DECODE — asked once, before a single byte is fetched.
 *
 * Video is COPIED THROUGH, never re-encoded (docs/PLAYER.md, and the whole reason one
 * cheap pod can serve every room). So a release this engine cannot decode is not a
 * slow path, it is a dead end — and until now we found that out the expensive way:
 * `probe.ts` fetches 16 KB, demuxes the header, builds an RFC 6381 codec string, asks
 * `MediaSource.isTypeSupported`, and throws `unsupported-video-codec`. Per candidate.
 * Every time. On a machine where the answer was never going to change.
 *
 * Meanwhile the ranking had an OPINION about codecs and no knowledge: the service
 * scores `x264` 30 and `x265` 12 from the filename (internal/catalog/parse.ts), on
 * every device equally. docs/LINKS-AUDIT.md measured what that costs: in a sweep of
 * 250 top-ranked candidates, name-derived HEVC releases were **26 % dead** against
 * **54 % for H.264** — so the blanket HEVC penalty aims the walk at the deader half of
 * the corpus, on a Mac where HEVC is hardware-decoded and the better copy.
 *
 * The fix is not a different guess. It is to ASK:
 *
 *   - `MediaSource.isTypeSupported` is free, synchronous, and the same function the
 *     probe already trusts. Asking it once at startup for five codec strings gives an
 *     honest capability profile for THIS engine.
 *   - The release NAME then only has to answer "which codec is this likely to be",
 *     which is a much easier question than "will it play".
 *
 * What this deliberately does NOT do: reject anything. A name is a guess — a file
 * called `x265` can be H.264 and vice versa — so an unsupported guess is a RANKING
 * penalty, and the probe stays the only thing allowed to say no. Being wrong here
 * costs a position in a list; being wrong in the probe costs a film.
 *
 * Pure apart from the injected detector, so it tests without a browser.
 */

/* ────────────────────────────────────────────────────────────────────────────
 * Codec identity, from a release name
 * ──────────────────────────────────────────────────────────────────────────── */

/** Video codecs the demuxer can name and the muxer can emit. */
export type VideoCodec = 'h264' | 'hevc' | 'av1' | 'vp9';

/** Audio codecs that matter to the decision. Everything else is `other`. */
export type AudioCodec = 'aac' | 'opus' | 'flac' | 'ac3' | 'eac3' | 'dts' | 'truehd' | 'other';

/**
 * Guess the video codec from a release name. `null` means the name does not say,
 * which is the honest answer for a third of the corpus and must never be read as
 * "bad" — most unlabelled releases are H.264.
 */
export function guessVideoCodec(releaseName: string): VideoCodec | null {
  const t = releaseName.replace(/[._]/g, ' ');
  if (/\b(x265|h\.?265|hevc|hvc1|hev1)\b/i.test(t)) return 'hevc';
  if (/\bav1\b/i.test(t)) return 'av1';
  if (/\b(vp9|vp09)\b/i.test(t)) return 'vp9';
  if (/\b(x264|h\.?264|avc|avc1)\b/i.test(t)) return 'h264';
  return null;
}

/**
 * Guess the audio codec from the parsed hints and the release name. Order matters:
 * "DTS-HD MA" must not be read as "DTS", and "TrueHD Atmos" is TrueHD.
 */
export function guessAudioCodec(hints: readonly string[], releaseName = ''): AudioCodec | null {
  const hay = `${hints.join(' ')} ${releaseName.replace(/[._]/g, ' ')}`;
  if (/\b(truehd|mlp)\b/i.test(hay)) return 'truehd';
  if (/\bdts\b|\bdts-hd\b|\bdts-x\b|\bdts:x\b/i.test(hay)) return 'dts';
  if (/\b(eac3|e-ac3|ddp|dd\+)\b/i.test(hay)) return 'eac3';
  if (/\b(ac3|dd5|dd\b|dolby ?digital)\b/i.test(hay)) return 'ac3';
  if (/\bflac\b/i.test(hay)) return 'flac';
  if (/\bopus\b/i.test(hay)) return 'opus';
  if (/\baac\b/i.test(hay)) return 'aac';
  return null;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Capability detection
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * The codec strings to ask about. These are the same shapes `web/src/lib/media/
 * codecs.ts` builds from the container's own configuration record, with the baseline
 * profile every real release clears:
 *
 *  - `avc1.640028` — H.264 High@4.0. The floor of the corpus, supported everywhere.
 *  - `hvc1.1.6.L93.B0` — HEVC Main@L3.1, and the fourcc our muxer emits (never `hev1`).
 *    Safari decodes it in hardware; Chrome only where the PLATFORM has a decoder
 *    (so: usually yes on Windows/macOS, usually no on Linux); Firefox mostly not.
 *  - `av01.0.05M.08` — AV1 Main, level 3.1. Chrome and Firefox decode it in software;
 *    Safari only on hardware that has it.
 *  - `vp09.00.10.08` — VP9 Profile 0.
 *
 * Audio is asked the same way, and the answers only decide COST, never possibility:
 * anything MediaSource will not take is decoded to AAC-LC by libav in the worker.
 */
const VIDEO_PROBES: Record<VideoCodec, string> = {
  h264: 'video/mp4; codecs="avc1.640028"',
  hevc: 'video/mp4; codecs="hvc1.1.6.L93.B0"',
  av1: 'video/mp4; codecs="av01.0.05M.08"',
  vp9: 'video/mp4; codecs="vp09.00.10.08"'
};

const AUDIO_PROBES: Partial<Record<AudioCodec, string>> = {
  aac: 'audio/mp4; codecs="mp4a.40.2"',
  opus: 'audio/mp4; codecs="opus"',
  flac: 'audio/mp4; codecs="flac"',
  ac3: 'audio/mp4; codecs="ac-3"',
  eac3: 'audio/mp4; codecs="ec-3"'
};

export interface EngineCapabilities {
  /** Video codecs MediaSource will accept here. A false is a hard "cannot play". */
  video: Record<VideoCodec, boolean>;
  /** Audio codecs MediaSource will accept WITHOUT the wasm transcode. */
  audio: Partial<Record<AudioCodec, boolean>>;
}

/**
 * The conservative profile used when nothing can be asked (server-side render, a
 * test, a browser with no MediaSource at all). H.264 only: it is the one codec that
 * has never not worked, and assuming less than the truth costs a ranking position
 * while assuming more costs a failed play.
 */
export const ASSUME_BASELINE: EngineCapabilities = {
  video: { h264: true, hevc: false, av1: false, vp9: false },
  audio: { aac: true }
};

/** The same shape as `defaultIsTypeSupported` in `web/src/lib/media/probe.ts`. */
export type TypeSupported = (mime: string) => boolean;

function browserIsTypeSupported(): TypeSupported | null {
  if (typeof MediaSource !== 'undefined' && MediaSource.isTypeSupported) {
    return (mime) => MediaSource.isTypeSupported(mime);
  }
  // iPhone Safari exposes ManagedMediaSource instead of MediaSource.
  const MMS = (globalThis as { ManagedMediaSource?: typeof MediaSource }).ManagedMediaSource;
  if (MMS?.isTypeSupported) return (mime) => MMS.isTypeSupported(mime);
  return null;
}

/** Ask the engine what it can decode. Pure given `isTypeSupported`. */
export function detectCapabilities(isTypeSupported?: TypeSupported | null): EngineCapabilities {
  const ask = isTypeSupported ?? browserIsTypeSupported();
  if (ask === null) return ASSUME_BASELINE;
  const video = {} as Record<VideoCodec, boolean>;
  for (const [codec, mime] of Object.entries(VIDEO_PROBES) as Array<[VideoCodec, string]>) {
    video[codec] = safeAsk(ask, mime);
  }
  // H.264 is the floor. A detector that says no to it is broken or absent, and
  // believing it would rank every playable release as unplayable.
  if (!video.h264) return ASSUME_BASELINE;
  const audio: Partial<Record<AudioCodec, boolean>> = {};
  for (const [codec, mime] of Object.entries(AUDIO_PROBES) as Array<[AudioCodec, string]>) {
    audio[codec] = safeAsk(ask, mime);
  }
  return { video, audio };
}

function safeAsk(ask: TypeSupported, mime: string): boolean {
  try {
    return ask(mime) === true;
  } catch {
    return false;
  }
}

let cached: EngineCapabilities | null = null;

/** The detected profile for this page, asked once. */
export function capabilities(): EngineCapabilities {
  cached ??= detectCapabilities();
  return cached;
}

/** Tests and the dev harness only. */
export function setCapabilitiesForTest(caps: EngineCapabilities | null): void {
  cached = caps;
}

/* ────────────────────────────────────────────────────────────────────────────
 * The verdict a ranking can use
 * ──────────────────────────────────────────────────────────────────────────── */

export type Playability =
  /** Both tracks pass straight through. Fastest start there is. */
  | 'native'
  /** Video passes through; audio needs the libav wasm decode to AAC-LC. */
  | 'transcode-audio'
  /** The name says a codec this engine cannot decode. Probably unplayable HERE. */
  | 'unsupported-video'
  /** The name does not say enough. Most of these are H.264 and play fine. */
  | 'unknown';

export interface PlayabilityVerdict {
  playability: Playability;
  video: VideoCodec | null;
  audio: AudioCodec | null;
  /** True when the audio track will cost the 2.3 MB libav chunk and a decode pass. */
  transcodesAudio: boolean;
  /** Plain words, for whoever is explaining a ranking. '' when there is nothing to say. */
  note: string;
}

/**
 * What this browser is likely to make of a release, from its NAME alone.
 *
 * Everything here is a likelihood, which is why the vocabulary is 'unsupported' and
 * not 'unplayable': the probe remains the only thing that may refuse a file.
 */
export function playabilityOf(
  input: { title: string; audioHints?: readonly string[] },
  caps: EngineCapabilities = capabilities()
): PlayabilityVerdict {
  const video = guessVideoCodec(input.title);
  const audio = guessAudioCodec(input.audioHints ?? [], input.title);

  if (video !== null && caps.video[video] === false) {
    return {
      playability: 'unsupported-video',
      video,
      audio,
      transcodesAudio: false,
      note: `${label(video)} — this browser has no decoder for it`
    };
  }

  // Absent from the table (an exotic codec) is treated as "needs the transcode",
  // which is what the worker does with everything it does not recognise as passthrough.
  const audioNative = audio === null ? null : (caps.audio[audio] ?? false);
  const transcodesAudio = audio !== null && audioNative === false;

  if (video === null) {
    return {
      playability: 'unknown',
      video: null,
      audio,
      transcodesAudio,
      note: transcodesAudio ? `${label(audio)} audio is converted in the browser` : ''
    };
  }

  return {
    playability: transcodesAudio ? 'transcode-audio' : 'native',
    video,
    audio,
    transcodesAudio,
    note: transcodesAudio ? `${label(audio)} audio is converted in the browser` : `${label(video)}, plays as-is`
  };
}

function label(codec: VideoCodec | AudioCodec | null): string {
  switch (codec) {
    case 'h264':
      return 'H.264';
    case 'hevc':
      return 'HEVC';
    case 'av1':
      return 'AV1';
    case 'vp9':
      return 'VP9';
    case 'aac':
      return 'AAC';
    case 'opus':
      return 'Opus';
    case 'flac':
      return 'FLAC';
    case 'ac3':
      return 'Dolby Digital';
    case 'eac3':
      return 'Dolby Digital Plus';
    case 'dts':
      return 'DTS';
    case 'truehd':
      return 'TrueHD';
    default:
      return 'Audio';
  }
}
